import * as React from 'react'
import { AutoCompleteInput, Button, Checkbox, ColorPicker, IconButton, Input, Label, Select } from 'uxp/components'
import ActionEditorLink, { extractAction, IAction } from '../action-editor/ActionEditor'
import { applyAtPath, IBaseProperty, IUIPropertyDefinition } from '../common'
import { BackgroundPositionEditor, CustomColorPicker, escapeUnit, Property, PropertyContainer, PropertyContainerTitle, PropertyGroup, PropertyGroupTitle, PropertyTitle, TextAlignEditor } from './common'
import { IBackgroundProps, ITextProps } from './properties'
import jp from 'jsonpath'
import { hasProperty, hasValue } from '../../../util'
import { ImagePicker } from 'lucy/common-components'
import { Conditional } from '../ConditionalComponent'

export interface IAxisLabelProps {
    value?: string,
    offset?: number,
    position?: 'insideTopLeft' | 'insideLeft' | 'insideBottomLeft' | 'insideBottom' | 'insideBottomRight'
}

export interface IChartAxisLabelProperty extends IBaseProperty {
    axis?: 'x' | 'y'
    field?: string,
    showAllLabels?: boolean,
    rotation?: number,
    rotateDirection?: 'clockwise' | 'anti-clockwise',
    showDecimals?: boolean,
    label?: IAxisLabelProps
}


export function extractAxisLabelProps(props: IChartAxisLabelProperty) {
    const labelProperties: any = {}

    if (!props) props = {}
    if (!props?.axis) props.axis = 'x'
    if (props?.showAllLabels) labelProperties.interval = 0

    if (hasValue(props?.rotateDirection) && hasValue(props?.rotation)) {
        let rotation = props?.rotateDirection == 'clockwise' ? props?.rotation : -(props?.rotation)
        labelProperties.angle = rotation
        labelProperties.textAnchor = props?.rotateDirection == 'clockwise' ? 'start' : 'end'
    }
    else if (props?.rotation == 0 && props?.axis == 'x') {
        labelProperties.textAnchor = 'middle'
    }

    if (hasValue(props?.label?.value)) {

        labelProperties.label = {
            value: props.label.value,
            offset: props.label?.offset || 0,
            position: props.label?.position || (props.axis == 'x' ? 'insideBottomRight' : 'insideTopLeft')
        }

        if (props.axis == 'y') {
            labelProperties.label.angle = -90
        }
    }

    if (props?.axis == 'y') labelProperties.allowDecimals = props?.showDecimals || false

    return labelProperties
}

export function ChartAxisLabelProperty(defaultProps: IChartAxisLabelProperty, getAvailableFields: () => string[], title?: string,): IUIPropertyDefinition<IChartAxisLabelProperty> {
    return {
        defaults: defaultProps,
        render: (props, uxpContext, contextData, containerId) => {

            let _props = props?.uiProps || {}
            if (!_props.axis) _props.axis = 'x'
            if (!_props.rotateDirection) _props.rotateDirection = 'clockwise'
            if (!_props.rotation) _props.rotation = 0
            if (!_props.label) _props.label = { value: '', offset: 0, position: _props.axis == 'x' ? 'insideBottomRight' : 'insideTopLeft' }
            if (!_props.label?.value) _props.label.value = ''
            if (!_props.label?.offset) _props.label.offset = 0
            if (!_props.label?.position) _props.label.position = _props.axis == 'x' ? 'insideBottomRight' : 'insideTopLeft'

            if (_props.axis == 'x') {
                if (!_props.field) _props.field = ''
                if (!_props.showAllLabels) _props.showAllLabels = false
            }
            else if (_props.axis == 'y') {
                if (!_props.showDecimals) _props.showDecimals = false
            }

            const yLabelPotitions = [
                { label: 'Top', value: 'insideTopLeft' },
                { label: 'Middle', value: 'insideLeft' },
                { label: 'Bottom', value: 'insideBottomLeft' },
            ]
            const xLabelPotitions = [
                { label: 'Left', value: 'insideBottomLeft' },
                { label: 'Middle', value: 'insideBottom' },
                { label: 'Right', value: 'insideBottomRight' },
            ]
            const options = _props.axis == 'x' ? xLabelPotitions : yLabelPotitions

            containerId = hasValue(containerId) ? containerId + '-' : ''

            return <PropertyContainer key={_props.axis}>
                <PropertyContainerTitle>{title || `${_props.axis == 'x' ? 'X' : 'Y'} Axis Label`}</PropertyContainerTitle>
                <PropertyGroup>


                    <Conditional visible={_props.axis == 'x'}>
                        <Property id={containerId + 'field'} >
                            <PropertyTitle>X axis field</PropertyTitle>
                            <AutoCompleteInput
                                options={getAvailableFields()}
                                value={_props.field || ''}
                                onChange={s => {
                                    let newProps = applyAtPath(_props, '$.field', () => (s))
                                    props.onChange(newProps)
                                }}
                                className="widget-designer-input"
                            />
                        </Property>

                        <Property id={containerId + 'show-all'} >
                            <Checkbox
                                checked={_props.showAllLabels}
                                onChange={s => {
                                    let newProps = applyAtPath(_props, '$.showAllLabels', () => (s))
                                    props.onChange(newProps)
                                }}
                                className="widget-designer-input"
                                label='Show all labels'
                                type="bordered"
                            />
                        </Property>
                    </Conditional>

                    <Property id={containerId + 'label-angle'} >
                        <PropertyTitle>Label angle (in Degree) </PropertyTitle>
                        <Input
                            type='number'
                            value={(_props.rotation || 0).toString()}
                            onChange={s => {
                                let num = Number(s) || 0
                                if (num < 0) num = 0
                                else if (num > 180) num = 180
                                let newProps = applyAtPath(_props, '$.rotation', () => (num))
                                props.onChange(newProps)
                            }}

                            inputAttr={{
                                max: '180',
                                min: '0'
                            }}
                        />
                    </Property>

                    <Property id={containerId + 'label-angle-direction'} >
                        <PropertyTitle>Label angle direction</PropertyTitle>
                        <Select
                            selected={_props.rotateDirection}
                            onChange={s => {
                                let newProps = applyAtPath(_props, '$.rotateDirection', () => (s))
                                props.onChange(newProps)
                            }}
                            options={[
                                { label: 'Clockwise ', value: 'clockwise' },
                                { label: 'Anti clockwise ', value: 'anti-clockwise' },
                            ]}
                        />
                    </Property>

                    <Property id={containerId + 'axis-label'} >
                        <PropertyTitle>Axis Label</PropertyTitle>
                        <Input
                            value={_props?.label.value || ''}
                            onChange={s => {
                                let newProps = applyAtPath(_props, '$.label.value', () => (s))
                                props.onChange(newProps)
                            }}
                        />
                    </Property>

                    <Property id={containerId + 'axis-label-offset'} >
                        <PropertyTitle>Label offset </PropertyTitle>
                        <Input
                            type='number'
                            value={(_props?.label?.offset || 0).toString()}
                            onChange={s => {
                                let num = Number(s) || 0
                                // if (num < 0) num = 0
                                // else if (num > 180) num = 180
                                let newProps = applyAtPath(_props, '$.label.offset', () => (num))
                                props.onChange(newProps)
                            }}

                            inputAttr={{
                                // max: '180',
                                // min: '0'
                            }}
                        />
                    </Property>

                    <Property id={containerId + 'axis-label-offset'} >
                        <PropertyTitle>Label Position </PropertyTitle>
                        <Select
                            selected={_props?.label?.position}
                            onChange={s => {
                                let newProps = applyAtPath(_props, '$.label.position', () => (s))
                                props.onChange(newProps)
                            }}
                            options={options}
                        />
                    </Property>


                    <Conditional visible={_props.axis == 'y'}>
                        <Checkbox
                            checked={_props.showDecimals || false}
                            onChange={s => {
                                let newProps = applyAtPath(_props, '$.showDecimals', () => (s))
                                props.onChange(newProps)
                            }}
                            label='Show decimal values'
                        />
                    </Conditional>


                </PropertyGroup>
            </PropertyContainer>
        }
    }

}